import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type GrownUp } from "../lib/api";
import { Field } from "../routes/Setup";

/* Who can stand behind the counter.
 *
 * Every grown-up here is the same grown-up: there is no owner account, and the
 * adult who ran setup has no standing the second one lacks. The panel says so
 * out loud, because "can they see my kids' money?" is the first thing anyone
 * adding a second adult wants answered, and a permissions matrix nobody reads
 * is not an answer.
 *
 * Removed adults stay on the list rather than vanishing. They are still the
 * name on transactions the family can read back, and this is where you go to
 * let one in again.
 */
export function GrownUps({ onCancel }: { onCancel: () => void }) {
  const [rows, setRows] = useState<GrownUp[] | null>(null);
  const [form, setForm] = useState<Form>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await api.get<GrownUp[]>("/grown-ups"));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function remove(id: string) {
    setBusy(true);
    setError(null);
    try {
      await api.del(`/grown-ups/${id}`);
      setConfirming(null);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That didn't work.");
    } finally {
      setBusy(false);
    }
  }

  const here = rows?.filter((g) => g.isActive) ?? [];
  const gone = rows?.filter((g) => !g.isActive) ?? [];

  return (
    <section className="card" style={{ marginTop: 20 }}>
      <h2 style={{ fontSize: 18, marginBottom: 4 }}>Grown-ups</h2>
      <p style={{ margin: "0 0 16px", fontSize: 13.5, color: "var(--ink-2)" }}>
        Everyone here can do everything: check off jobs, hand out cash, move
        money and change what the kids can see.
      </p>

      {rows === null ? (
        <p style={{ color: "var(--ink-3)", margin: 0 }}>Loading&hellip;</p>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {here.map((g) => (
            <Row key={g.id} who={g}>
              {!g.isYou && confirming !== g.id && (
                <span className="slip-do">
                  <button onClick={() => { setConfirming(g.id); setError(null); }}>
                    Remove
                  </button>
                </span>
              )}
              {confirming === g.id && (
                <>
                  <span className="slip-do">
                    <button
                      disabled={busy}
                      onClick={() => remove(g.id)}
                      style={{ background: "var(--give-soft)", color: "var(--give-deep)" }}
                    >
                      {busy ? "Removing…" : `Remove ${g.displayName}`}
                    </button>
                    <button disabled={busy} onClick={() => setConfirming(null)}>
                      Keep them
                    </button>
                  </span>
                  <p style={{ flexBasis: "100%", margin: 0, fontSize: 13, color: "var(--ink-2)" }}>
                    They&rsquo;ll be signed out everywhere and their password
                    stops working. Everything they&rsquo;ve already done stays in
                    the record, and you can let them back in later with a new
                    password.
                  </p>
                </>
              )}
            </Row>
          ))}
        </div>
      )}

      {gone.length > 0 && (
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid var(--paper-edge)" }}>
          <p className="cap">No longer signs in</p>
          <p className="cap-hint">
            Their name still appears on everything they did.
          </p>
          <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
            {gone.map((g) => (
              <Row key={g.id} who={g} faded>
                <span className="slip-do">
                  <button onClick={() => { setForm({ restore: g }); setError(null); }}>
                    Let them back in
                  </button>
                </span>
              </Row>
            ))}
          </div>
        </div>
      )}

      {error && (
        <p role="alert" style={{ color: "var(--bad)", fontWeight: 600, marginTop: 14 }}>
          {error}
        </p>
      )}

      <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--paper-edge)" }}>
        {form ? (
          <GrownUpForm
            restore={form.restore}
            onCancel={() => setForm(null)}
            onDone={async () => {
              setForm(null);
              await load();
            }}
          />
        ) : (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button
              onClick={() => { setForm({}); setError(null); }}
              style={{ flex: "1 1 160px", minHeight: 48, fontSize: 15 }}
            >
              Add a grown-up
            </button>
            <button onClick={onCancel} style={{ minHeight: 48, fontSize: 15 }}>
              Done
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

/** Which form is open: none, a fresh add, or a restore of one person. */
type Form = { restore?: GrownUp } | null;

function Row({
  who,
  faded,
  children,
}: {
  who: GrownUp;
  faded?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div
      className="slip"
      style={{ ["--slip-hue" as string]: "var(--ink-3)", opacity: faded ? 0.7 : 1 }}
    >
      <span className="slip-what">
        <span style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 600 }}>
          {who.displayName}
        </span>
        {who.isYou && <span style={{ color: "var(--ink-3)" }}> &middot; you</span>}
        <span className="money" style={{ display: "block", fontSize: 12.5, color: "var(--ink-3)" }}>
          {who.loginName}
          {who.isActive && !who.hasSignedIn && " · hasn't signed in yet"}
        </span>
      </span>
      {children}
    </div>
  );
}

/* Adding and letting back in are the same two questions -- a password for
 * them, and yours -- so they are the same form. A restore does not ask for a
 * name or a sign-in name because it keeps the record it already has: that is
 * what makes their old transactions still resolve to the same person. */
function GrownUpForm({
  restore,
  onDone,
  onCancel,
}: {
  restore?: GrownUp;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    displayName: "",
    loginName: "",
    password: "",
    yourPassword: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function set(key: keyof typeof form) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [key]: e.target.value });
  }

  const tooShort = form.password.length > 0 && form.password.length < 8;
  const ready =
    form.password.length >= 8 &&
    form.yourPassword.length > 0 &&
    (restore
      ? true
      : form.displayName.trim().length > 0 && form.loginName.trim().length >= 2);

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          if (restore) {
            await api.post(`/grown-ups/${restore.id}/restore`, {
              password: form.password,
              yourPassword: form.yourPassword,
            });
          } else {
            await api.post("/grown-ups", {
              ...form,
              loginName: form.loginName.trim().toLowerCase(),
            });
          }
          onDone();
        } catch (err) {
          setError(err instanceof ApiError ? err.message : "That didn't work.");
        } finally {
          setBusy(false);
        }
      }}
      style={{ display: "grid", gap: 12 }}
    >
      <h3 style={{ fontSize: 16 }}>
        {restore ? `Let ${restore.displayName} back in` : "Add a grown-up"}
      </h3>

      {restore ? (
        <p style={{ margin: 0, fontSize: 13.5, color: "var(--ink-2)" }}>
          They&rsquo;ll sign in as{" "}
          <span className="money" style={{ fontWeight: 700 }}>{restore.loginName}</span>{" "}
          again. The password they had was wiped when they were removed, so pick
          a new one.
        </p>
      ) : (
        <>
          <Field label="Their name">
            <input value={form.displayName} onChange={set("displayName")} autoFocus
                   required maxLength={60} placeholder="Robin" />
          </Field>
          <Field label="Their sign-in name" hint="What they tap on the sign-in screen. No spaces.">
            <input value={form.loginName} onChange={set("loginName")} required
                   minLength={2} autoCapitalize="none" autoCorrect="off"
                   placeholder="robin" />
          </Field>
        </>
      )}

      <Field
        label={restore ? "A new password for them" : "A password for them"}
        hint="At least 8 characters. They can change it once they're in."
      >
        <input type="password" value={form.password} onChange={set("password")}
               required autoFocus={Boolean(restore)} autoComplete="new-password" />
      </Field>
      <Field label="Your password" hint="Asked because this hands out access that keeps working after the tablet is locked.">
        <input type="password" value={form.yourPassword} onChange={set("yourPassword")}
               required autoComplete="current-password" />
      </Field>

      {tooShort && (
        <p style={{ margin: 0, color: "var(--bad)", fontWeight: 600, fontSize: 14 }}>
          That&rsquo;s too short. Use at least 8 characters.
        </p>
      )}
      {error && (
        <p role="alert" style={{ margin: 0, color: "var(--bad)", fontWeight: 600 }}>
          {error}
        </p>
      )}

      <div style={{ display: "flex", gap: 10 }}>
        <button type="submit" disabled={busy || !ready} style={{ flex: 1 }}>
          {busy
            ? restore ? "Letting them in…" : "Adding…"
            : restore ? `Let ${restore.displayName} back in` : "Add grown-up"}
        </button>
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
